import { all, takeEvery, put, call } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { IRequestParams, request } from 'helpers/request';
import { CONSTANTS, logSagaErrorMessage } from 'utils';
import { getAdminEndpoint, requestSagaFactory } from 'helpers';
import * as types from 'modules/admin-panel/pages/templates/ActionTypes';
import {
	deleteTemplate,
	getTemplateData,
	getTemplateList,
	ITemplateDataAction,
	postMinimalTemplate,
	postTemplate,
	updateTemplateData,
} from 'modules/admin-panel/pages/templates/templates.actions';
import {
	ITemplate,
	ITemplateFormData,
	ITemplateDB,
	ITemplateData,
} from 'modules/admin-panel/pages/templates/models';
import { mapTemplateList } from 'modules/admin-panel/pages/templates/helpers/mapTemplateList';

const templateApiPath = `${CONSTANTS.moduleSurveyPath}/templates`;

const ENDPOINTS = {
	templateList: getAdminEndpoint(templateApiPath),
	templateData: getAdminEndpoint(`${templateApiPath}/{{templateId}}`),
};

function* postTemplateSaga({
	payload: { templateData },
}: {
	payload: {
		templateData: ITemplateFormData;
	};
}) {
	try {
		yield put(postTemplate.loading());

		const postTemplatesParams: IRequestParams = {
			method: 'POST',
			url: ENDPOINTS.templateList,
			data: JSON.stringify(templateData),
		};

		const { data }: AxiosResponse<ITemplate> = yield call(
			request,
			postTemplatesParams,
		);

		yield put(postTemplate.success(data));
	} catch (e) {
		logSagaErrorMessage('postTemplate', e);
		yield put(postTemplate.fail(e));
	}
}

function* postMinimalTemplateSaga({
	payload,
}: {
	payload: {
		name: string;
		isQuick: boolean;
	};
}) {
	try {
		yield put(postMinimalTemplate.loading());

		const postMinimalTemplateParams: IRequestParams = {
			method: 'POST',
			url: `${ENDPOINTS.templateList}/minimal`,
			data: JSON.stringify(payload),
		};

		const { data }: AxiosResponse<ITemplate> = yield call(
			request,
			postMinimalTemplateParams,
		);

		yield put(postMinimalTemplate.success(data));
	} catch (e) {
		logSagaErrorMessage('postMinimalTemplate', e);
		yield put(postMinimalTemplate.fail(e));
	}
}

function* getTemplateListSaga() {
	try {
		yield put(getTemplateList.loading());

		const getTemplateListParams = {
			url: ENDPOINTS.templateList,
		};

		const { data }: AxiosResponse<ITemplateDB[]> = yield call(
			request,
			getTemplateListParams,
		);

		yield put(getTemplateList.success(mapTemplateList(data)));
	} catch (e) {
		logSagaErrorMessage('getTemplateList', e);
		yield put(getTemplateList.fail(e));
	}
}

export const getTemplateDataSaga = requestSagaFactory<
	ITemplateDataAction,
	ITemplateData
>({
	url: ENDPOINTS.templateData,
	actionApi: getTemplateData,
	logErrorMessage: 'getTemplateData',
});

function* updateTemplateSaga({
	payload: { templateData, templateId },
}: {
	payload: {
		templateId: string;
		templateData: ITemplateFormData;
	};
}) {
	try {
		yield put(updateTemplateData.loading());

		const updateTemplateParams: IRequestParams = {
			method: 'PATCH',
			url: getAdminEndpoint(`${templateApiPath}/${templateId}`),
			data: JSON.stringify(templateData),
		};

		yield call(request, updateTemplateParams);

		yield put(updateTemplateData.success());
	} catch (e) {
		logSagaErrorMessage('updateTemplateData', e);
		yield put(updateTemplateData.fail(e));
	}
}

function* deleteTemplatesSaga({
	payload: { id, showAlert },
}: {
	payload: {
		id: number;
		showAlert: boolean;
	};
}) {
	try {
		yield put(deleteTemplate.loading());

		const deleteTemplateParams: IRequestParams = {
			method: 'DELETE',
			url: `${ENDPOINTS.templateList}/${id}`,
			data: id,
		};

		yield call(request, deleteTemplateParams);

		yield put(deleteTemplate.success({ id, showAlert }));
	} catch (e) {
		logSagaErrorMessage('deleteTemplate', e);
		yield put(deleteTemplate.fail(e));
	}
}

export function* templatesSaga() {
	yield all([
		takeEvery(types.GET_TEMPLATE_LIST.DEFAULT, getTemplateListSaga),
		takeEvery(types.GET_TEMPLATE_DATA.DEFAULT, getTemplateDataSaga),
		// @ts-ignore
		takeEvery(types.POST_TEMPLATE.DEFAULT, postTemplateSaga),
		// @ts-ignore
		takeEvery(types.POST_MINIMAL_TEMPLATE.DEFAULT, postMinimalTemplateSaga),
		// @ts-ignore
		takeEvery(types.PATCH_TEMPLATE_DATA.DEFAULT, updateTemplateSaga),
		// @ts-ignore
		takeEvery(types.DELETE_TEMPLATE.DEFAULT, deleteTemplatesSaga),
	]);
}
